import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, Chip, Tooltip, CircularProgress, Link, Stack, TableFooter, Box, TableRow, Table, TableHead, TableBody, TableCell, Divider, Grid2 as Grid, Typography, FormControl, InputLabel, Select, MenuItem, Accordion, AccordionSummary, AccordionDetails, TableContainer } from '@mui/material';
import Main from '../../../layouts/Main';
import Container from '../../../components/Container';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import BallotQuestionReport from '../../../types/reports/ballotQuestionReport';
import BallotQuestionCommittee from '../../../types/reports/ballotQuestionCommittee';
import { useErrorBoundary } from 'react-error-boundary';
import PageBreadCrumbs from '../../../components/PageBreadcrumbs';


const currencyFormatter = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

interface SummaryTileProps {
  label: string;
  value: string;
}

const SummaryTile = ({ label, value }: SummaryTileProps): JSX.Element => (
  <Card sx={{ borderRadius: 3, boxShadow: 2, height: '100%', backgroundColor: '#f7faff' }}>
    <CardContent>
      <Typography
        variant="caption"
        display="block"
        sx={{
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          color: 'text.secondary',
          fontSize: 10,
          mb: 0.25,
        }}
      >
        {label}
      </Typography>
      <Typography component="span" fontWeight={700} fontSize={20} sx={{ lineHeight: 1.1 }}>
        {value}
      </Typography>
    </CardContent>
  </Card>
);


const QUESTION_ALLOCATION_FIRST_YEAR = 2026;
// From the 2026 cycle the committee tables show spending only: the Start and
// Receipts columns are dropped for that year onward.
const SPENDING_ONLY_FIRST_YEAR = 2026;

const sumExpenditures = (committees: BallotQuestionCommittee[], useQuestionAllocation: boolean): number =>
  committees.reduce((committeesTotal, committee) => committeesTotal + (committee.reports || []).reduce(
    (reportsTotal, report) => reportsTotal +
      ((useQuestionAllocation ? report.questionExpendituresYtdNumeric : report.expendituresYtdNumeric) || 0), 0), 0);

const getQuestionStats = (ballotQuestion: BallotQuestionReport, useQuestionAllocation: boolean) => {
  const supportingCommittees = ballotQuestion.supportingCommittees || [];
  const opposingCommittees = ballotQuestion.opposingCommittees || [];

  return {
    committeeCount: supportingCommittees.length + opposingCommittees.length,
    supportCount: supportingCommittees.length,
    opposeCount: opposingCommittees.length,
    hasReports: [...supportingCommittees, ...opposingCommittees]
      .some((committee) => (committee.reports || []).length > 0),
    totalSpent: sumExpenditures(supportingCommittees, useQuestionAllocation) +
      sumExpenditures(opposingCommittees, useQuestionAllocation),
  };
};

const renderQuestionHeader = (ballotQuestion: BallotQuestionReport, useQuestionAllocation: boolean): JSX.Element => {
  const { committeeCount, supportCount, opposeCount, hasReports, totalSpent } =
    getQuestionStats(ballotQuestion, useQuestionAllocation);

  return (
    <Stack direction="row" alignItems="center" spacing={2} sx={{ width: '100%', paddingRight: 1 }}>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="h6" component="h1">{ballotQuestion.description}</Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {committeeCount === 0
            ? 'No committees organized yet'
            : `${committeeCount} committee${committeeCount === 1 ? '' : 's'} \u00b7 ${supportCount} support / ${opposeCount} oppose`}
        </Typography>
      </Box>
      <Box sx={{ flexGrow: 1 }} />
      <Typography
        sx={{
          fontWeight: totalSpent > 0 ? 700 : 400,
          color: hasReports ? 'text.primary' : 'text.secondary',
          whiteSpace: 'nowrap',
        }}
      >
        {hasReports ? currencyFormatter.format(totalSpent) : '\u2014'}
      </Typography>
    </Stack>
  );
};


const BallotQuestionReports = (): JSX.Element => {

  document.title = 'OCPF - Ballot Question Committee Reports';

  const [ballotQuestions, setBallotQuestions] = useState<BallotQuestionReport[]>([]);
  const { showBoundary } = useErrorBoundary();
  const [isLoading, setIsLoading] = useState(true);
  const dropDownYears = Array.from(new Array(10), (val, index) => new Date().getFullYear() % 2 === 0 ? new Date().getFullYear()- index * 2 : new Date().getFullYear() - 1 - index * 2);
  const [reportYear, setReportYear] = useState(() => {
    if (new Date().getFullYear() % 2 === 0 ) {
      return new Date().getMonth() <= 5 ? new Date().getFullYear() - 2 : new Date().getFullYear(); // BQ ballot list comes out around July/August .
    }

    return new Date().getFullYear() - 1;
  });

  const updateReporYear = (event: any) => {
    setReportYear(event.target.value);
  };


  const getBallotQuestions = async () => {
    try {
      await fetch(`${process.env.REACT_APP_API_BASE_URL}/reports/ballotQuestions/byQuestion/${reportYear}`)
        .then(result => {
          if (!result.ok) {
            showBoundary(new Error('Failed to get ballot question reports.'));
          }
          return result.json();
        })
        .then(result => {
          setBallotQuestions(result);
          setIsLoading(false);
        });
    } catch (error) {
      console.error('Uncaught Error:', error);
      showBoundary(new Error(`${error}`));
    }
  }

  useEffect(() => {
    getBallotQuestions();
  }, [reportYear]);




  const summary = useMemo(() => {
    let totalRaised = 0;
    let totalSpent = 0;
    const countedCommitteeIds = new Set<number>();

    ballotQuestions.forEach((ballotQuestion: BallotQuestionReport) => {
      const committees = [
        ...(ballotQuestion.supportingCommittees || []),
        ...(ballotQuestion.opposingCommittees || []),
      ];

      committees.forEach((committee) => {
        if (countedCommitteeIds.has(committee.cpfId)) {
          return; // a committee can back more than one question; only count its reports once.
        }
        countedCommitteeIds.add(committee.cpfId);

        (committee.reports || []).forEach((report) => {
          totalRaised += report.receiptsYtdNumeric || 0;
          totalSpent += report.expendituresYtdNumeric || 0;
        });
      });
    });

    return {
      totalRaised,
      totalSpent,
      committeeCount: countedCommitteeIds.size,
      questionCount: ballotQuestions.length,
    };
  }, [ballotQuestions]);

  const summaryTiles: SummaryTileProps[] = [
    { label: 'Total ballot questions', value: `${summary.questionCount}` },
    { label: 'Total raised, all questions', value: currencyFormatter.format(summary.totalRaised) },
    { label: 'Total spent, all questions', value: currencyFormatter.format(summary.totalSpent) },
    { label: 'Committees registered', value: `${summary.committeeCount}` },
  ];


  // Committees can be organized around more than one question; flag those so the
  // same name showing up under several questions is obvious.
  const multiQuestionCommitteeIds = useMemo(() => {
    const questionCountsByCommittee = new Map<number, number>();

    ballotQuestions.forEach((ballotQuestion: BallotQuestionReport) => {
      const committeeIds = new Set<number>([
        ...(ballotQuestion.supportingCommittees || []),
        ...(ballotQuestion.opposingCommittees || []),
      ].map((committee) => committee.cpfId));

      committeeIds.forEach((cpfId) => {
        questionCountsByCommittee.set(cpfId, (questionCountsByCommittee.get(cpfId) || 0) + 1);
      });
    });

    const multiQuestionIds = new Set<number>();
    questionCountsByCommittee.forEach((questionCount, cpfId) => {
      if (questionCount > 1) {
        multiQuestionIds.add(cpfId);
      }
    });

    return multiQuestionIds;
  }, [ballotQuestions]);

  const hasMultiQuestionCommittee = (ballotQuestion: BallotQuestionReport): boolean =>
    [
      ...(ballotQuestion.supportingCommittees || []),
      ...(ballotQuestion.opposingCommittees || []),
    ].some((committee) => multiQuestionCommitteeIds.has(committee.cpfId));

  const showQuestionAllocation = reportYear >= QUESTION_ALLOCATION_FIRST_YEAR;
  const showReceiptColumns = reportYear < SPENDING_ONLY_FIRST_YEAR;

  const renderCommittee = (filer: any, disposition: 'support' | 'oppose') => {
    const hasReports = filer.reports && filer.reports.length > 0;
    const isMultiQuestionCommittee = (filer.reports || []).some((report: any) => report.questionCount > 1);
    // Cash on hand is a running balance, so only the latest year's figure is meaningful.
    const latestReportYear = (filer.reports || []).reduce(
      (latest: number, report: any) => Math.max(latest, report.reportYear || 0), 0);
    return (
      <Box key={filer.cpfId + '-committee'} sx={{ marginBottom: 3 }}>
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ marginBottom: 1 }}>
          <Chip
            label={disposition === 'support' ? 'SUPPORT' : 'OPPOSE'}
            size="small"
            sx={{
              backgroundColor: disposition === 'support' ? '#1565c0' : '#c9940c',
              color: '#fff',
              fontWeight: 700,
              fontSize: '0.7rem',
              letterSpacing: '0.5px',
              borderRadius: '4px',
            }}
          />
          <Stack direction="row" spacing={0.25} alignItems="flex-start">
            <Link href={'/filers?q=' + filer.cpfId} target="_blank" underline="hover" >
              <Typography key={filer.cpfId}><b>{filer.committeeName}</b></Typography>
            </Link>
            {multiQuestionCommitteeIds.has(filer.cpfId) &&
              <Tooltip title="This committee supports or opposes more than one ballot question">
                <Typography component="span" sx={{ color: 'text.secondary', fontWeight: 700, fontSize: 20, lineHeight: 1 }}>*</Typography>
              </Tooltip>
            }
          </Stack>
          <Typography sx={{ color: 'text.secondary' }}>{filer.cpfId}</Typography>
          <Box sx={{ flexGrow: 1 }} />
          <Typography key={filer.cpfId + '-url'}>
            <Link href={'/filers?q=' + filer.cpfId + '&section=Reports'} target="_blank" underline="hover">Committee Reports &rarr;</Link>
          </Typography>
        </Stack>
        {!hasReports &&
          <Box sx={{ border: '1px dashed', borderColor: 'grey.400', padding: 1.5 }}>
            <Typography variant="body2" color="text.secondary">
              This committee has not filed a report yet.
            </Typography>
          </Box>
        }
        {hasReports &&
        <TableContainer key={filer.cpfId + '-table-container'}>
          <Table>
          <TableHead>
            <TableRow key={filer.cpfId + '-table-header'}>
              <TableCell sx={{ fontWeight: 700 }}>Year</TableCell>
              {showReceiptColumns &&
                <>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>Start</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>Receipts</TableCell>
                </>
              }
              <TableCell align="right" sx={{ fontWeight: 700 }}>{showQuestionAllocation ? 'General Expenditures' : 'Expenditures'}</TableCell>
              {showQuestionAllocation &&
                <TableCell align="right" sx={{ fontWeight: 700 }}>Question Specific Expenditures **</TableCell>
              }
              {/* From 2026 the cash figure closes the row as "Cash on Hand"; earlier
                  years keep "End" in its original place. */}
              {!showQuestionAllocation &&
                <TableCell align="right" sx={{ fontWeight: 700 }}>End</TableCell>
              }
              <TableCell align="right" sx={{ fontWeight: 700 }}>{showQuestionAllocation ? 'General Inkinds' : 'Inkinds'}</TableCell>
              {showQuestionAllocation &&
                <>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>Question Specific Inkinds **</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>Cash on Hand</TableCell>
                </>
              }
            </TableRow>
          </TableHead>
          <TableBody>
            {filer.reports.map((report: any, index: number) => (
              <TableRow key={filer.cpfId + '-report-' + index}>
                <TableCell>{report.reportYear}</TableCell>
                {showReceiptColumns &&
                  <>
                    <TableCell align="right">{report.startBalance}</TableCell>
                    <TableCell align="right">{report.receiptsYtd}</TableCell>
                  </>
                }
                <TableCell align="right">{showQuestionAllocation ? report.generalExpenditureShare : report.expendituresYtd}</TableCell>
                {showQuestionAllocation &&
                  <TableCell align="right">
                    {report.questionCount > 1 ? report.questionSpecificExpendituresYtd : '–'}
                  </TableCell>
                }
                {!showQuestionAllocation &&
                  <TableCell align="right">{report.currentCashOnHand}</TableCell>
                }
                <TableCell align="right">{showQuestionAllocation ? report.generalInkindShare : report.inkindsYtd}</TableCell>
                {showQuestionAllocation &&
                  <>
                    <TableCell align="right">
                      {report.questionCount > 1 ? report.questionSpecificInkindsYtd : '–'}
                    </TableCell>
                    <TableCell align="right">
                      {report.reportYear === latestReportYear ? report.currentCashOnHand : ''}
                    </TableCell>
                  </>
                }
              </TableRow>
            ))}
          </TableBody>
            <TableFooter>
              <TableRow key={filer.cpfId + '-footer'}>
              <TableCell>{disposition === 'support' ? 'Support' : 'Oppose'} Total:</TableCell>
              {showReceiptColumns &&
                <>
                  <TableCell></TableCell>
                  <TableCell align="right">
                    <Typography variant='body2'>
                      {
                        new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(filer.reports.reduce((accumulator: number, current: any) => accumulator + current.receiptsYtdNumeric, 0))
                      }
                    </Typography>
                  </TableCell>
                </>
              }
              <TableCell align="right">
                <Typography variant='body2'>
                  {
                    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(filer.reports.reduce((accumulator: number, current: any) => accumulator + (showQuestionAllocation ? current.generalExpenditureShareNumeric : current.expendituresYtdNumeric), 0))
                  }
                </Typography>
              </TableCell>
              {showQuestionAllocation &&
                <TableCell align="right">
                  <Typography variant='body2'>
                    {
                      isMultiQuestionCommittee
                        ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(filer.reports.reduce((accumulator: number, current: any) => accumulator + current.questionSpecificExpendituresYtdNumeric, 0))
                        : '–'
                    }
                  </Typography>
                </TableCell>
              }
              {!showQuestionAllocation &&
                <TableCell></TableCell>
              }
              <TableCell align="right">
                <Typography variant='body2'>
                  {
                    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(filer.reports.reduce((accumulator: number, current: any) => accumulator + (showQuestionAllocation ? current.generalInkindShareNumeric : current.inkindsYtdNumeric), 0))
                  }
                </Typography>
              </TableCell>
              {showQuestionAllocation &&
                <>
                  <TableCell align="right">
                    <Typography variant='body2'>
                      {
                        isMultiQuestionCommittee
                          ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(filer.reports.reduce((accumulator: number, current: any) => accumulator + current.questionSpecificInkindsYtdNumeric, 0))
                          : '–'
                      }
                    </Typography>
                  </TableCell>
                  <TableCell></TableCell>
                </>
              }
              </TableRow>
            </TableFooter>
          </Table>
        </TableContainer>
        }
      </Box>
    );
  };

  return (
    <Main>
      <Container>
      <PageBreadCrumbs></PageBreadCrumbs>
        <Typography
          variant="h5"
          align={'left'}
          data-aos={'fade-up'}
          gutterBottom
          sx={{
            fontWeight: 700,
          }}
          paddingTop={3}
          marginBottom={2}
        >
          Ballot Question Committee Reports
        </Typography>
        <Divider />
        <Grid container marginBottom={2} marginTop={3}>
          <Grid size={12}>
            <FormControl sx={{ m: 1, minWidth: 120 }} size="small">
              <InputLabel id="year-id">Year</InputLabel>
              <Select
                sx={{ fontSize: 'smaller' }}
                labelId="year-id"
                id="year-select"
                aria-labelledby='year-id'
                label="Report Year"
                value={reportYear}
                onChange={updateReporYear}
              >
                {
                  dropDownYears.map((year) =>
                    <MenuItem value={year} key={year} >{year}</MenuItem>
                  )
                }
              </Select>
            </FormControl>
          </Grid>
          {ballotQuestions.length > 0 &&
            <Grid size={12} marginTop={2}>
              <Grid container spacing={3}>
                {summaryTiles.map((tile) => (
                  <Grid key={tile.label} size={{ xs: 12, sm: 6, lg: 3 }}>
                    <SummaryTile {...tile} />
                  </Grid>
                ))}
              </Grid>
            </Grid>
          }
          {ballotQuestions.length > 0 &&
            <Grid size={12} marginTop={3}>
              <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between" sx={{ paddingRight: '48px' }}>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Click a question to view its support / oppose committees
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                  Total spent
                </Typography>
              </Stack>
              {showQuestionAllocation &&
                <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic', marginTop: 0.5 }}>
                  If a ballot question committee is organized to support or oppose two or more questions,
                  its full reported amounts are divided evenly across the committee&apos;s questions, unless the
                  expenditure or inkind is reported by the committee for a specific question, and is then counted
                  as the question specific expenditures/inkinds.
                </Typography>
              }
            </Grid>
          }
          <Grid size={12} marginTop={3}>

            {ballotQuestions.map((ballotQuestion: BallotQuestionReport) => (
              <Accordion key={ballotQuestion.id}>
                <AccordionSummary key={ballotQuestion.id + '-summary'}
                  expandIcon={<ArrowDropDownIcon />}
                  aria-controls="panel2-content"
                  id="panel2-header"
                >
                  {renderQuestionHeader(ballotQuestion, showQuestionAllocation)}
                </AccordionSummary>
                <AccordionDetails key={ballotQuestion.id + '-details'}>
                  {
                    ballotQuestion.supportingCommittees && ballotQuestion.supportingCommittees.length > 0 &&
                    <Box key={ballotQuestion.id + '-support-table-container'} sx={{ m: 2 }}>
                      {ballotQuestion.supportingCommittees.map((filer: any) => (
                        renderCommittee(filer, 'support')
                      ))}
                    </Box>
                  }

                  {ballotQuestion.opposingCommittees && ballotQuestion.opposingCommittees.length > 0 &&
                    <Box key={ballotQuestion.id + '-oppose-table-container'} sx={{ m: 2 }}>
                      {ballotQuestion.opposingCommittees.map((filer: any) => (
                        renderCommittee(filer, 'oppose')
                      ))}
                    </Box>
                  }

                  {showQuestionAllocation && hasMultiQuestionCommittee(ballotQuestion) &&
                    <Typography variant="body2" sx={{ color: 'text.secondary', mx: 2, mb: 1 }}>
                      * This committee is organized to support or oppose two or more questions. Its full
                      reported amounts are divided evenly across those questions, unless the expenditure or inkind
                      is reported by the committee for a specific question, and is then counted as the question
                      specific expenditures/inkinds.
                    </Typography>
                  }

                  {showQuestionAllocation &&
                    ((ballotQuestion.supportingCommittees || []).length > 0 ||
                      (ballotQuestion.opposingCommittees || []).length > 0) &&
                    <Typography variant="body2" sx={{ color: 'text.secondary', mx: 2, mb: 1 }}>
                      ** Question specific expenditures and inkinds only apply to a committee organized to
                      support or oppose more than one question. A dash means the committee is organized around
                      a single question, so none of its spending or in-kind contributions has to be allocated.
                    </Typography>
                  }

                </AccordionDetails>
              </Accordion>
            ))}
          </Grid>
        </Grid>

         {/*loading indicator*/}
               <CircularProgress
                    size={50}
                    className={isLoading? "": "hidden"}
                    sx={{
                      position: "fixed",
                      left: "50%",
                      top: "20%",
                      transform: "translate(-50%, -50%)",
                      zIndex: 2
                     }}
                />
      </Container>
    </Main>
  );
};

export default BallotQuestionReports;
